// Client tipado de `retail-org-business` (SureKeep F1 · componente 04 · pista Retail).
// EJE BACKEND (ARQUITECTURA_FRONT_PROPUESTA.md §7): una función por operationId del OpenAPI.
// Dueño de esta carpeta = quien trabaja el lambda (Andrés) → agregar aquí no colisiona con nadie.
//
// TIPOS — de dónde salen y por qué:
//   Los `*.gen.ts` de este lambda dan las RUTAS, pero NO las entidades: el OpenAPI declara todas
//   las respuestas como `ApiResponse` genérico (`data: Record<string, never>`). Ver TD-SKF1-011.
//   Por eso las entidades se importan de `@fiado/type-kit` — que es lo que manda el componente 10
//   ("Importar tipos desde @fiado/type-kit/<dominio> directamente"; nunca redeclarar shapes aquí).
//
//   ⚠️ REGLA DE IMPORT (bundle): `import type` para los DTOs — TS los borra en build, costo CERO.
//   NUNCA importar el barrel `@fiado/type-kit/bin/retailOrg` como valor: re-exporta los DTOs y
//   arrastra `class-validator` + `class-transformer` al bundle del browser. Los enums sí se pueden
//   importar como valor, pero SIEMPRE por deep-path (son módulos standalone, sin dependencias).
import type { Retailer } from '@fiado/type-kit/bin/retailOrg/dtos/Retailer';
import type { Zone } from '@fiado/type-kit/bin/retailOrg/dtos/Zone';
import type { Store } from '@fiado/type-kit/bin/retailOrg/dtos/Store';
import type { RetailUser } from '@fiado/type-kit/bin/retailOrg/dtos/RetailUser';
import type { ProvisionRetailUserRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/ProvisionRetailUserRequest';
import type { PaginatedResult } from '@fiado/type-kit/bin/retailOrg/dtos/PaginatedResult';
import type { RetailOrg } from '@fiado/type-kit';
import { apiFetch } from '../../client';

const BASE_URL = import.meta.env.VITE_API_RETAIL_ORG_BASE_URL;

/** Toda llamada de este servicio va a SU base path — no al del rbac. */
function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return apiFetch<T>(path, { ...init, baseUrl: BASE_URL });
}

function withIdempotency(key?: string): RequestInit {
    return key ? { headers: { 'Idempotency-Key': key } } : {};
}

export type { Retailer, Zone, Store, RetailUser };

// ---------------------------------------------------------------------------
// Retailers  (B01 — mock M6_ADMIN §2)
// ---------------------------------------------------------------------------

/** Tope de páginas de `/retailers`. Un backend que devolviera cursor sin fin colgaría la pantalla. */
const MAX_RETAILER_PAGES = 20;

/**
 * `publicListRetailers` — GET /retailers. Sigue el cursor `nextToken` hasta agotarlo y devuelve
 * TODAS las cadenas, porque sus consumidores afirman cosas sobre el conjunto completo («la cadena
 * del enlace no es tuya», «no tienes ninguna»): con media lista esas frases son falsas.
 * Pasado el tope de páginas lanza en vez de devolver una lista parcial como si fuera completa.
 */
export async function listRetailers(): Promise<Retailer[]> {
    const all: Retailer[] = [];
    let nextToken: string | undefined;

    for (let page = 0; page < MAX_RETAILER_PAGES; page++) {
        const query = nextToken === undefined ? '' : `?nextToken=${encodeURIComponent(nextToken)}`;
        const result = await call<PaginatedResult<Retailer>>(`/retailers${query}`);
        all.push(...result.items);
        if (result.nextToken === undefined) return all;
        nextToken = result.nextToken;
    }

    throw new Error(
        `El listado de cadenas no terminó de paginar en ${String(MAX_RETAILER_PAGES)} páginas.`,
    );
}

/** `publicGetRetailer` — GET /retailers/{retailerId}. */
export function getRetailer(retailerId: string): Promise<Retailer> {
    return call<Retailer>(`/retailers/${retailerId}`);
}

/** `publicCreateRetailer` — POST /retailers. Test 2 (crear "VentasLuga"). */
export function createRetailer(
    body: CreateRetailerInput,
    idempotencyKey?: string,
): Promise<Retailer> {
    return call<Retailer>('/retailers', {
        method: 'POST',
        body: JSON.stringify(body),
        ...withIdempotency(idempotencyKey),
    });
}

/** `publicUpdateRetailer` — PUT /retailers/{retailerId}. */
export function updateRetailer(
    retailerId: string,
    body: Partial<CreateRetailerInput>,
): Promise<Retailer> {
    return call<Retailer>(`/retailers/${retailerId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/** `publicChangeRetailerStatus` — PUT /retailers/{retailerId}/status. */
export function changeRetailerStatus(retailerId: string, status: string): Promise<Retailer> {
    return call<Retailer>(`/retailers/${retailerId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
    });
}

/** `publicCreateSubdistributor` — POST /retailers/{parentId}/subdistributors. */
export function createSubdistributor(
    parentId: string,
    body: CreateRetailerInput,
    idempotencyKey?: string,
): Promise<Retailer> {
    return call<Retailer>(`/retailers/${parentId}/subdistributors`, {
        method: 'POST',
        body: JSON.stringify(body),
        ...withIdempotency(idempotencyKey),
    });
}

/** `publicGetRetailerHierarchy` — GET /retailers/{retailerId}/hierarchy. Tab "Jerarquía". */
export function getRetailerHierarchy(retailerId: string): Promise<RetailerHierarchy> {
    return call<RetailerHierarchy>(`/retailers/${retailerId}/hierarchy`);
}

// ---------------------------------------------------------------------------
// Zonas  (Test 3)
// ---------------------------------------------------------------------------

/** `publicListZones` — GET /retailers/{retailerId}/zones. */
export function listZones(retailerId: string): Promise<Zone[]> {
    return call<Zone[]>(`/retailers/${retailerId}/zones`);
}

/** `publicCreateZone` — POST /retailers/{retailerId}/zones. */
export function createZone(
    retailerId: string,
    body: CreateZoneInput,
    idempotencyKey?: string,
): Promise<Zone> {
    return call<Zone>(`/retailers/${retailerId}/zones`, {
        method: 'POST',
        body: JSON.stringify(body),
        ...withIdempotency(idempotencyKey),
    });
}

/** `publicUpdateZone` — PUT /retailers/{retailerId}/zones/{zoneId}. */
export function updateZone(
    retailerId: string,
    zoneId: string,
    body: Partial<CreateZoneInput>,
): Promise<Zone> {
    return call<Zone>(`/retailers/${retailerId}/zones/${zoneId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/** `publicChangeZoneStatus` — PUT /retailers/{retailerId}/zones/{zoneId}/status. */
export function changeZoneStatus(
    retailerId: string,
    zoneId: string,
    status: string,
): Promise<Zone> {
    return call<Zone>(`/retailers/${retailerId}/zones/${zoneId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
    });
}

// ---------------------------------------------------------------------------
// Stores  (Test 3)
// ---------------------------------------------------------------------------

/** `publicListStores` — GET /retailers/{retailerId}/stores. */
export function listStores(retailerId: string): Promise<Store[]> {
    return call<Store[]>(`/retailers/${retailerId}/stores`);
}

/** `publicGetStore` — GET /retailers/{retailerId}/stores/{storeId}. */
export function getStore(retailerId: string, storeId: string): Promise<Store> {
    return call<Store>(`/retailers/${retailerId}/stores/${storeId}`);
}

/** `publicCreateStore` — POST /retailers/{retailerId}/stores. `location` lat/lng es OBLIGATORIA. */
export function createStore(
    retailerId: string,
    body: CreateStoreInput,
    idempotencyKey?: string,
): Promise<Store> {
    return call<Store>(`/retailers/${retailerId}/stores`, {
        method: 'POST',
        body: JSON.stringify(body),
        ...withIdempotency(idempotencyKey),
    });
}

/** `publicUpdateStore` — PUT /retailers/{retailerId}/stores/{storeId}. */
export function updateStore(
    retailerId: string,
    storeId: string,
    body: Partial<CreateStoreInput>,
): Promise<Store> {
    return call<Store>(`/retailers/${retailerId}/stores/${storeId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/** `publicChangeStoreStatus` — PUT /retailers/{retailerId}/stores/{storeId}/status. */
export function changeStoreStatus(
    retailerId: string,
    storeId: string,
    status: string,
): Promise<Store> {
    return call<Store>(`/retailers/${retailerId}/stores/${storeId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
    });
}

// ---------------------------------------------------------------------------
// RetailUsers  (Test 4)
// ---------------------------------------------------------------------------

/** `publicListRetailUsers` — GET /retailers/{retailerId}/users. */
export function listRetailUsers(retailerId: string): Promise<RetailUser[]> {
    return call<RetailUser[]>(`/retailers/${retailerId}/users`);
}

/** `publicGetRetailUser` — GET /retailers/{retailerId}/users/{retailUserId}. */
export function getRetailUser(retailerId: string, retailUserId: string): Promise<RetailUser> {
    return call<RetailUser>(`/retailers/${retailerId}/users/${retailUserId}`);
}

/**
 * `publicCreateRetailUser` — POST /retailers/{retailerId}/users.
 * El backend valida el `cognitoSub` contra platform-rbac (F0) vía api-invoker: si el user no tiene
 * un rol con scope a este retailer devuelve 412 `UserDoesNotHaveRetailerRole`.
 */
export function createRetailUser(
    retailerId: string,
    body: CreateRetailUserInput,
    idempotencyKey?: string,
): Promise<RetailUser> {
    return call<RetailUser>(`/retailers/${retailerId}/users`, {
        method: 'POST',
        body: JSON.stringify(body),
        ...withIdempotency(idempotencyKey),
    });
}

/**
 * `publicProvisionRetailUser` — POST /retailers/{retailerId}/users/provision.
 * Alta ORQUESTADA (scope mirror, B4): en vez de un `cognitoSub` pre-existente, manda `email` + `roleId`
 * + `scope` + `scopeRef`; el backend provisiona el usuario en RBAC (Cognito + user + grant, queda INVITED)
 * y crea el RetailUser con el `cognitoSub` retornado. Un solo click en vez de dar de alta en RBAC aparte.
 */
export function provisionRetailUser(
    retailerId: string,
    body: ProvisionRetailUserInput,
    idempotencyKey?: string,
): Promise<RetailUser> {
    return call<RetailUser>(`/retailers/${retailerId}/users/provision`, {
        method: 'POST',
        body: JSON.stringify(body),
        ...withIdempotency(idempotencyKey),
    });
}

/** `publicUpdateRetailUser` — PUT /retailers/{retailerId}/users/{retailUserId}. */
export function updateRetailUser(
    retailerId: string,
    retailUserId: string,
    body: Partial<CreateRetailUserInput>,
): Promise<RetailUser> {
    return call<RetailUser>(`/retailers/${retailerId}/users/${retailUserId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/** `publicChangeRetailUserStatus` — PUT /retailers/{retailerId}/users/{retailUserId}/status. */
export function changeRetailUserStatus(
    retailerId: string,
    retailUserId: string,
    status: string,
): Promise<RetailUser> {
    return call<RetailUser>(`/retailers/${retailerId}/users/${retailUserId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
    });
}

/** `publicDeleteRetailUser` — DELETE /retailers/{retailerId}/users/{retailUserId}. Borrado físico. */
export async function deleteRetailUser(retailerId: string, retailUserId: string): Promise<void> {
    await call<null>(`/retailers/${retailerId}/users/${retailUserId}`, { method: 'DELETE' });
}

/** `publicTransferRetailUser` — PUT /retailers/{retailerId}/users/{retailUserId}/transfer. */
export function transferRetailUser(
    retailerId: string,
    retailUserId: string,
    homeStoreId: string,
): Promise<RetailUser> {
    return call<RetailUser>(`/retailers/${retailerId}/users/${retailUserId}/transfer`, {
        method: 'PUT',
        body: JSON.stringify({ homeStoreId }),
    });
}

// ---------------------------------------------------------------------------
// Wallet PCF del usuario retail
// ---------------------------------------------------------------------------

/** `publicLookupRetailUserWallet` — POST /retailers/{retailerId}/users/wallet-lookup. Solo lee. */
export function lookupRetailUserWallet(
    retailerId: string,
    body: RetailOrg.LookupRetailUserWalletRequest,
): Promise<RetailOrg.LookupRetailUserWalletResponse> {
    return call<RetailOrg.LookupRetailUserWalletResponse>(
        `/retailers/${retailerId}/users/wallet-lookup`,
        { method: 'POST', body: JSON.stringify(body) },
    );
}

/** `publicLinkRetailUserWallet` — PUT /retailers/{retailerId}/users/{retailUserId}/wallet. */
export function linkRetailUserWallet(
    retailerId: string,
    retailUserId: string,
    body: RetailOrg.LinkRetailUserWalletRequest,
): Promise<RetailUser> {
    return call<RetailUser>(`/retailers/${retailerId}/users/${retailUserId}/wallet`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/** `publicUnlinkRetailUserWallet` — DELETE /retailers/{retailerId}/users/{retailUserId}/wallet. */
export function unlinkRetailUserWallet(
    retailerId: string,
    retailUserId: string,
): Promise<RetailUser> {
    return call<RetailUser>(`/retailers/${retailerId}/users/${retailUserId}/wallet`, {
        method: 'DELETE',
    });
}

// ---------------------------------------------------------------------------
// Inputs — derivados de las entidades de type-kit (NO se redeclaran shapes).
// ---------------------------------------------------------------------------

/** Campos que el backend genera/gestiona: no viajan en el request de creación. */
type Managed = 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'status';

// `retailerId` viaja en el path, no en el body → se omite de los inputs anidados.
export type CreateRetailerInput = Omit<Retailer, 'retailerId' | Managed>;
export type CreateZoneInput = Omit<Zone, 'zoneId' | 'retailerId' | Managed>;
export type CreateStoreInput = Omit<Store, 'storeId' | 'retailerId' | Managed>;
export type CreateRetailUserInput = Omit<RetailUser, 'retailUserId' | 'retailerId' | Managed>;
export type ProvisionRetailUserInput = ProvisionRetailUserRequest;

/** Respuesta de `publicGetRetailerHierarchy` — árbol Retailer → Zone → Store → User. */
export interface RetailerHierarchy {
    retailer: Retailer;
    zones: (Zone & { stores: (Store & { users: RetailUser[] })[] })[];
    /** Stores sin zona (subdistribuidores, `zoneId=null`). */
    unzonedStores: (Store & { users: RetailUser[] })[];
}
